import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import type {FC} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {Icon} from 'src/common/Icon'
import type {Context} from 'src/common/types'

export type ExpensePolicySummaryProps = SliceComponentProps<Content.ExpensePolicySummarySlice>

const ExpensePolicySummary: FC<ExpensePolicySummaryProps & {context: Context}> = ({
  slice,
  context: {pageUid, buttonsData},
  index: componentIndex,
}) => {
  const {linkResolver} = useLinkResolver()
  const sanitizedHeading = sanitizeHtml(slice.primary.heading ?? '')

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
  })

  return (
    <Container
      backgroundColor="Moss White"
      className="flex flex-col !gap-0 !pb-12 !pt-[122px] sm:!gap-[60px] md:!pt-[137px] xl:!gap-0"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      extraPaddingTop
    >
      <div className="mx-auto grid w-full max-w-[1200px] scroll-mt-16 grid-cols-4 gap-4 gap-y-12 px-5 md:grid-cols-12 md:gap-x-6 md:px-10 lg:px-5 xl:px-0">
        <div className="col-span-full flex flex-col gap-y-18 md:grid md:grid-cols-subgrid md:gap-y-0">
          <div
            className="max-md:grid-base grid items-center !gap-y-6 md:col-span-6 md:grid-cols-subgrid md:items-start md:self-center"
            data-aos="fade-up"
            data-aos-offset="0"
          >
            <div className="col-span-4 grid grid-cols-subgrid gap-6 text-center md:col-span-full md:grid-cols-subgrid md:text-start">
              {isFilled.keyText(slice.primary.subheading_with_icon) && (
                <div className="col-span-full flex justify-center md:justify-start">
                  <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-moss-white py-1 pl-1 pr-3">
                    <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-default">
                      <Icon name="check-mark" className="size-2.5 text-moss-white" />
                    </div>
                    <p className="text-[14px] font-medium leading-4 text-moss-black">
                      {slice.primary.subheading_with_icon}
                    </p>
                  </div>
                </div>
              )}
              <h2
                className="text-h2 col-span-full text-black lg:text-[48px] lg:leading-14"
                dangerouslySetInnerHTML={{__html: sanitizedHeading}}
              />
              {isFilled.keyText(slice.primary.subtext) && (
                <p className="text-tg-body-m md:text-tg-body-l col-span-4 text-moss-grey-500">
                  {slice.primary.subtext}
                </p>
              )}
              {isFilled.group(slice.primary.features) && (
                <div className="col-span-full flex flex-col gap-2">
                  {slice.primary.features.map((item, index) => (
                    <div className="flex items-center gap-3" key={`feature-${index}`}>
                      <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-moss-grey-400">
                        <Icon name="check-mark" className="size-2 text-moss-white" />
                      </div>
                      <p className="text-body-m text-[15px] text-moss-grey-500">{item.feature_label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-full flex flex-wrap justify-center gap-2 md:justify-start md:gap-4">
              {primaryButtonData && (
                <ButtonV2
                  variant="primary"
                  href={linkResolver(primaryButtonData.link, 'primary-button-expense-policy-summary')}
                  newTrackingProps={{
                    interaction: {
                      element: primaryButtonData.marketing_id || 'primary-button-expense-policy-summary',
                      elementText: primaryButtonData.label || '',
                      location: `component-${componentIndex}-expense-policy-summary`,
                    },
                    pageType: pageUid || 'unknown',
                  }}
                  trackingData={{
                    location: `component-${componentIndex}-expense-policy-summary`,
                    dataType: 'primary-button',
                    pageName: pageUid || 'unknown',
                    mktgId: primaryButtonData.marketing_id || '',
                    label: primaryButtonData.label!,
                  }}
                  id={`component-${componentIndex}-expense-policy-summary-1`}
                  className="uppercase"
                >
                  {primaryButtonData.label}
                </ButtonV2>
              )}
            </div>
            {isFilled.keyText(slice.primary.bottom_comment) && (
              <p className="text-body-s col-span-full text-moss-grey-400">{slice.primary.bottom_comment}</p>
            )}
          </div>
          {isFilled.image(slice.primary.image) && (
            <div
              className="max-md:grid-base grid gap-y-4 self-center md:col-span-6 md:col-start-7 md:grid-cols-subgrid md:gap-y-6"
              data-aos="fade-up"
              data-aos-offset="0"
            >
              <div className="col-span-full overflow-hidden rounded-lg">
                <PrismicNextImage field={slice.primary.image} alt="" className="size-full object-cover" priority />
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  )
}

export default ExpensePolicySummary
